<template>
  <div class="ml-2">
    <loading :active.sync="isLoading" :can-cancel="true" :opacity="0.85" :is-full-page="true"></loading>
    <div class="row">
      <div class="mt-3 pl-5 col-lg-2 col-md-4 col-sm-12">Asset Type *</div>
      <div class="col-lg-6 col-md-6 col-sm-12">
        <multiselect
          v-model="selectedAssetType"
          :options="assetTypeList"
          placeholder="Select asset type"
          :showLabels="false"
          label="AssetTypeName"
          track-by="Id"
          :searchable="true"
          @input="assetTypeChanged"
        >
          <span slot="noResult">No results</span>
          <span slot="noOptions">No asset types with assets</span>
        </multiselect>
      </div>
    </div>
    <div class="row mt-3" v-show="selectedAssetType">
      <div class="mt-3 pl-5 col-lg-2 col-md-4 col-sm-12">Site *</div>
      <div class="col-lg-6 col-md-6 col-sm-12">
        <multiselect
          v-model="selectedSite"
          :options="siteList"
          placeholder="Select site"
          :showLabels="false"
          label="text"
          track-by="value"
          :searchable="true"
          :close-on-select="true"
          @input="siteChanged"
        >
          <span slot="noResult">No results</span>
          <span slot="noOptions">No sites with this asset type</span>
        </multiselect>
      </div>
    </div>
    <div class="row mt-3" v-show="selectedSite">
      <div class="mt-3 pl-5 col-lg-2 col-md-4 col-sm-12">Area *</div>
      <div class="col-lg-6 col-md-6 col-sm-12">
        <multiselect
          v-model="selectedArea"
          :options="areaList"
          placeholder="Select area"
          :showLabels="false"
          label="AreaName"
          track-by="id"
          :searchable="true"
          :close-on-select="true"
          @input="areaChanged"
        >
          <span slot="noResult">No results</span>
          <span slot="noOptions">No areas with this asset type</span>
        </multiselect>
      </div>
    </div>
    <div class="row mt-3" v-show="selectedArea">
      <div class="mt-3 pl-5 col-lg-2 col-md-4 col-sm-12">Asset *</div>
      <div class="col-lg-6 col-md-6 col-sm-12">
        <multiselect
          v-model="selectedAsset"
          :options="assetList"
          placeholder="Select asset"
          :showLabels="false"
          label="text"
          track-by="value"
          :searchable="true"
          :close-on-select="true"
        >
          <span slot="noResult">No results</span>
          <span slot="noOptions">No assets found</span>
        </multiselect>
      </div>
    </div>
    <div class="row mt-3">
      <div class="mt-3 pl-5 col-lg-2 col-md-4 col-sm-12">Dates *</div>
      <div class="col-lg-6 col-md-6 col-sm-12 datepicker-trigger">
        <input
          type="text"
          id="single-asset-dates-trigger"
          placeholder="Select dates"
          :value="formatDates(startDate, endDate)"
        />
        <AirbnbStyleDatepicker
          :trigger-element-id="'single-asset-dates-trigger'"
          :mode="'range'"
          :showShortcutsMenuTrigger="false"
          :fullscreen-mobile="true"
          :end-date="maxDate"
          :date-one="startDate"
          :date-two="endDate"
          @date-one-selected="val => { startDate = val }"
          @date-two-selected="val => { endDate  = val }"
        />
      </div>
    </div>
    <div class="row mt-4">
      <div class="col-lg-8 col-md-10 col-sm-12 text-right">
        <button class="btn btn-action btn-round btn-primary" @click="viewReport">View</button>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import Multiselect from "vue-multiselect";
import axios from "axios";
import moment from "moment";
import format from "date-fns/format";
import Loading from "vue-loading-overlay";
import store from "@/router/store";
import { getCustomer } from "@/db";
import { getHost } from "../../env";

Vue.component("multiselect", Multiselect);

const selectDateFormat = "D MMM";
const longDateFormat = "D MMM YYYY";

export default {
  components: {
    Multiselect,
    Loading
  },
  data() {
    return {
      env: getHost(),
      isLoading: false,
      // Single source of truth: all assets are denormalized with
      // AssetTypeId/SiteId/SiteName/AreaId/AreaName/TagNo, so the whole
      // cascade (and the "has assets" filtering) derives from this.
      allAssets: [],
      allAssetTypes: [],
      selectedAssetType: null,
      selectedSite: null,
      selectedArea: null,
      selectedAsset: null,
      startDate: moment()
        .subtract(1, "year")
        .startOf("day")
        .toDate(),
      endDate: moment()
        .endOf("day")
        .toDate(),
      maxDate: moment().format("YYYY-MM-DD"),
      prettyDate: "",
      dateFormat: selectDateFormat,
      longDateFormat: longDateFormat
    };
  },
  firestore() {
    return {
      allAssets: getCustomer().collection("assets"),
      allAssetTypes: getCustomer()
        .collection("assettypes")
        .orderBy("AssetTypeName")
    };
  },
  computed: {
    // Asset type ids that actually have at least one asset (count > 0).
    typeIdsWithAssets() {
      return new Set(this.allAssets.map(a => a.AssetTypeId));
    },
    assetTypeList() {
      var present = this.typeIdsWithAssets;
      return this.allAssetTypes.filter(t => present.has(t.Id));
    },
    siteList() {
      if (!this.selectedAssetType) return [];

      var typeId = this.selectedAssetType.Id;
      var seen = new Map();

      this.allAssets.forEach(a => {
        if (a.AssetTypeId === typeId && !seen.has(a.SiteId)) {
          seen.set(a.SiteId, { text: a.SiteName, value: a.SiteId });
        }
      });

      return this.sortBy([...seen.values()], "text");
    },
    areaList() {
      if (!this.selectedAssetType || !this.selectedSite) return [];

      var typeId = this.selectedAssetType.Id;
      var siteId = this.selectedSite.value;
      var seen = new Map();

      this.allAssets.forEach(a => {
        if (a.AssetTypeId === typeId && a.SiteId === siteId && !seen.has(a.AreaId)) {
          seen.set(a.AreaId, { AreaName: a.AreaName, id: a.AreaId });
        }
      });

      return this.sortBy([...seen.values()], "AreaName");
    },
    assetList() {
      if (!this.selectedAssetType || !this.selectedArea) return [];

      var typeId = this.selectedAssetType.Id;
      var areaId = this.selectedArea.id;

      var result = this.allAssets
        .filter(a => a.AssetTypeId === typeId && a.AreaId === areaId)
        .map(a => {
          return { text: a.TagNo, value: a.Id };
        });

      return this.sortBy(result, "text");
    }
  },
  methods: {
    sortBy(list, key) {
      return list.sort((a, b) =>
        (a[key] || "").toUpperCase() > (b[key] || "").toUpperCase() ? 1 : -1
      );
    },
    formatDates(dateOne, dateTwo) {
      var retVal = "";

      if (dateOne && dateTwo) {
        retVal = `${format(dateOne, this.longDateFormat)} - ${format(
          dateTwo,
          this.longDateFormat
        )}`;
        this.prettyDate = `${format(dateOne, this.longDateFormat)} - ${format(
          dateTwo,
          this.longDateFormat
        )}`;
      }

      return retVal;
    },
    assetTypeChanged() {
      this.selectedSite = null;
      this.selectedArea = null;
      this.selectedAsset = null;
    },
    siteChanged() {
      this.selectedArea = null;
      this.selectedAsset = null;
    },
    areaChanged() {
      this.selectedAsset = null;
    },
    hideLoadingAtNextTick() {
      Vue.nextTick(() => {
        this.isLoading = false;
      });
    },
    viewReport() {
      if (!this.selectedAssetType) {
        this.$helpers.dispatch("showError", { msg: "Please select an asset type", vm: this });
        return;
      }
      if (!this.selectedSite) {
        this.$helpers.dispatch("showError", { msg: "Please select a site", vm: this });
        return;
      }
      if (!this.selectedArea) {
        this.$helpers.dispatch("showError", { msg: "Please select an area", vm: this });
        return;
      }
      if (!this.selectedAsset) {
        this.$helpers.dispatch("showError", { msg: "Please select an asset", vm: this });
        return;
      }
      if (!this.startDate || !this.endDate) {
        this.$helpers.dispatch("showError", { msg: "Please select a date range", vm: this });
        return;
      }

      var data = {
        DateGenerated: moment().format("DD-MMM-YYYY HH:mm"),
        CustomerId: store.getters.selectedCustomer.Id,
        CustomerName: store.getters.selectedCustomer.CustomerName,
        Client: this.env,
        AssetTypeId: this.selectedAssetType.Id,
        AssetType: this.selectedAssetType.AssetTypeName,
        SiteId: this.selectedSite.value,
        Site: this.selectedSite.text,
        AreaId: this.selectedArea.id,
        Area: this.selectedArea.AreaName,
        AssetId: this.selectedAsset.value,
        TagNo: this.selectedAsset.text
      };

      data.StartDate = moment(this.startDate).format(
        "YYYY-MM-DD 00:00:00.0000000 +02:00"
      );
      data.EndDate = moment(this.endDate).format(
        "YYYY-MM-DD 23:59:59.0000000 +02:00"
      );
      data.PrettyDate = this.prettyDate;

      let win = window.open("about:blank", "Single Asset Audit Report");
      var host = `${window.location.protocol}//${window.location.hostname}:${window.location.port}`;
      var loader = `<html><head><link type="text/css" href="${host}/loader.css" rel="stylesheet"></head><body><div class="loading"><div class="heading">Your report is being generated</div><div class="loader"></div></div></body></html>`;
      win.document.write(loader);
      var isLocal = ["localhost", "127.0.0.1"].includes(window.location.hostname);
      let reportUrl = isLocal
        ? "http://localhost:5136/reports/singleassetaudit"
        : "https://fema-reports.calmsmoke-626cd2e8.southafricanorth.azurecontainerapps.io/reports/singleassetaudit";

      axios
        .post(reportUrl, data, {
          responseType: "arraybuffer"
        })
        .then(resp => {
          var blob = new Blob([resp.data], { type: "application/pdf" });
          var blobUrl = window.URL.createObjectURL(blob);
          var html = `<html><style>html,body {padding:0;margin:0;} iframe {width:100%;height:100%;border:0}</style><body><iframe type="application/pdf" src="${blobUrl}"></iframe></body></html>`;
          win.document.getElementsByTagName("body")[0].innerHTML = "";
          win.document.write(html);
          win.document.close();
          win.onbeforeunload = function () {
            window.URL.revokeObjectURL(blobUrl);
          };
        })
        .catch(err => {
          win.close();
          this.$helpers.dispatch("showError", {
            msg: "Unexpected error occurred",
            vm: this
          });
        })
        .finally(f => {
          this.hideLoadingAtNextTick();
        });
    }
  }
};
</script>

<style scoped>
#single-asset-dates-trigger {
  width: 100%;
  min-height: 40px;
  padding-left: 10px;
  border-radius: 5px;
  border: 1px solid #e8e8e8;
}
</style>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
